// Mitarbeiter-Verwaltung — Beschäftigte je Firma (Mandant) + Arbeitsplatz.
// Klarnamen nur im RAM; gespeichert wird verschlüsselt (siehe mitarbeiterService).

import React, { useCallback, useEffect, useState } from 'react';
import { useMandantStore } from '../../../store/mandantStore';
import { useHandoffStore } from '../../../store/handoffStore';
import {
  loadMitarbeiter, saveMitarbeiter, deleteMitarbeiter,
  type MitarbeiterView, type MitarbeiterInput,
} from '../../../services/mitarbeiterService';
import { loadArbeitsplaetze } from '../../../services/vorsorgeService';
import type { Arbeitsplatz } from './ArbeitsplatzVerwaltung';

const GESCHLECHT_LABEL: Record<MitarbeiterView['geschlecht'], string> = {
  M: 'männlich', W: 'weiblich', D: 'divers', U: 'k.A.',
};

interface FormState {
  id?:          string;
  vorname:      string;
  nachname:     string;
  geburtsdatum: string;
  geschlecht:   MitarbeiterView['geschlecht'];
  arbeitsplatz_id: string;
  aktiv:        boolean;
}

const LEER: FormState = {
  vorname: '', nachname: '', geburtsdatum: '', geschlecht: 'U', arbeitsplatz_id: '', aktiv: true,
};

function MitarbeiterForm({ initial, arbeitsplaetze, onSave, onCancel }: {
  initial: FormState;
  arbeitsplaetze: Arbeitsplatz[];
  onSave: (f: FormState) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const [form, setForm] = useState<FormState>(initial);
  const valid = form.nachname.trim() !== '';

  return (
    <div className="card space-y-4">
      <h2 className="font-semibold text-gray-900 dark:text-gray-100">
        {initial.id !== undefined ? 'Mitarbeiter bearbeiten' : 'Neuer Mitarbeiter'}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="label-text">Vorname</label>
          <input value={form.vorname} onChange={e => { setForm(f => ({ ...f, vorname: e.target.value })); }}
            className="input-field" />
        </div>
        <div className="space-y-1">
          <label className="label-text">Nachname *</label>
          <input value={form.nachname} onChange={e => { setForm(f => ({ ...f, nachname: e.target.value })); }}
            className="input-field" />
        </div>
        <div className="space-y-1">
          <label className="label-text">Geburtsdatum</label>
          <input type="date" value={form.geburtsdatum}
            onChange={e => { setForm(f => ({ ...f, geburtsdatum: e.target.value })); }}
            className="input-field" />
        </div>
        <div className="space-y-1">
          <label className="label-text">Geschlecht</label>
          <select value={form.geschlecht}
            onChange={e => { setForm(f => ({ ...f, geschlecht: e.target.value as FormState['geschlecht'] })); }}
            className="input-field">
            <option value="U">k.A.</option>
            <option value="M">männlich</option>
            <option value="W">weiblich</option>
            <option value="D">divers</option>
          </select>
        </div>
      </div>
      <div className="space-y-1">
        <label className="label-text">Arbeitsplatz</label>
        <select value={form.arbeitsplatz_id}
          onChange={e => { setForm(f => ({ ...f, arbeitsplatz_id: e.target.value })); }}
          className="input-field">
          <option value="">— kein Arbeitsplatz —</option>
          {arbeitsplaetze.map(ap => (
            <option key={ap.id} value={ap.id ?? ''}>
              {ap.bezeichnung}{ap.abteilung ? ` (${ap.abteilung})` : ''}
            </option>
          ))}
        </select>
        {arbeitsplaetze.length === 0 && (
          <p className="text-xs text-amber-600">Noch keine Arbeitsplätze — im Tab „Arbeitsplätze" anlegen.</p>
        )}
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={form.aktiv}
          onChange={e => { setForm(f => ({ ...f, aktiv: e.target.checked })); }}
          className="rounded border-gray-300 text-blue-600" />
        <span className="text-sm text-gray-700 dark:text-gray-300">Aktiv (in Betreuung)</span>
      </label>
      <div className="flex gap-2">
        <button onClick={() => { onSave(form); }} disabled={!valid}
          className="btn-primary disabled:opacity-50">Speichern</button>
        <button onClick={onCancel} className="btn-secondary">Abbrechen</button>
      </div>
    </div>
  );
}

export function MitarbeiterVerwaltung(): React.JSX.Element {
  const mandant = useMandantStore(s => s.getAktiverMandant());
  const consume = useHandoffStore(s => s.consume);
  const [liste, setListe]     = useState<MitarbeiterView[]>([]);
  const [arbeitsplaetze, setArbeitsplaetze] = useState<Arbeitsplatz[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editForm, setEditForm] = useState<FormState>(LEER);
  const [fehler, setFehler]   = useState('');

  const reload = useCallback((): void => {
    if (mandant === null || mandant === undefined) { setListe([]); return; }
    void loadMitarbeiter(mandant.id)
      .then(m => { setListe(m); setFehler(''); })
      .catch(e => { setFehler(e instanceof Error ? e.message : String(e)); });
    void loadArbeitsplaetze(mandant.id).then(setArbeitsplaetze);
  }, [mandant]);

  useEffect(() => { reload(); }, [reload]);

  // Übergabe aus der Erfassung: „Als Mitarbeiter anlegen" → Formular vorbefüllt
  useEffect(() => {
    const h = consume();
    if (h !== null && h.action === 'mitarbeiter') {
      const d = h.data;
      setEditForm({
        ...LEER,
        vorname: d.vorname ?? '',
        nachname: d.nachname ?? '',
        geburtsdatum: d.geburtsdatum ?? '',
      });
      setShowForm(true);
    }
  }, [consume]);

  const handleSave = (f: FormState): void => {
    if (mandant === null || mandant === undefined) return;
    const input: MitarbeiterInput = {
      ...(f.id !== undefined ? { id: f.id } : {}),
      mandant_id: mandant.id,
      arbeitsplatz_id: f.arbeitsplatz_id,
      vorname: f.vorname, nachname: f.nachname,
      geburtsdatum: f.geburtsdatum, geschlecht: f.geschlecht, aktiv: f.aktiv,
    };
    void saveMitarbeiter(input)
      .then(() => { setShowForm(false); setEditForm(LEER); reload(); })
      .catch(e => { setFehler(e instanceof Error ? e.message : String(e)); });
  };

  const startEdit = (m: MitarbeiterView): void => {
    setEditForm({
      id: m.id, vorname: m.vorname, nachname: m.nachname,
      geburtsdatum: m.geburtsdatum, geschlecht: m.geschlecht,
      arbeitsplatz_id: m.arbeitsplatz_id, aktiv: m.aktiv,
    });
    setShowForm(true);
  };

  const handleDelete = (m: MitarbeiterView): void => {
    if (!window.confirm(`Mitarbeiter „${m.nachname} ${m.vorname}" löschen?`)) return;
    void deleteMitarbeiter(m.id).then(reload);
  };

  const apName = (id: string): string =>
    arbeitsplaetze.find(a => a.id === id)?.bezeichnung ?? '—';

  return (
    <div className="p-6 space-y-5 max-w-4xl mx-auto">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Mitarbeiter</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Beschäftigte
            {mandant !== null && mandant !== undefined
              ? <span className="ml-1 font-medium" style={{ color: mandant.farbe }}>{mandant.name}</span>
              : ' — bitte Firma wählen'}
            {' '}· Namen verschlüsselt gespeichert
          </p>
        </div>
        {mandant !== null && mandant !== undefined && (
          <button onClick={() => { setEditForm(LEER); setShowForm(true); }} className="btn-primary">
            + Mitarbeiter
          </button>
        )}
      </div>

      {fehler !== '' && (
        <p className="text-sm text-red-600 bg-red-50 dark:bg-red-950/20 rounded-lg p-3">⚠ {fehler}</p>
      )}

      {showForm && mandant !== null && mandant !== undefined && (
        <MitarbeiterForm
          initial={editForm}
          arbeitsplaetze={arbeitsplaetze}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditForm(LEER); }}
        />
      )}

      {mandant === null || mandant === undefined ? (
        <div className="card text-center py-12 text-gray-400">
          <p className="text-4xl mb-2">🏢</p>
          <p className="text-sm">Bitte oben links eine Firma auswählen.</p>
        </div>
      ) : liste.length === 0 && !showForm ? (
        <div className="card text-center py-12 space-y-2 text-gray-400">
          <p className="text-4xl">👥</p>
          <p className="font-medium text-gray-600 dark:text-gray-400">Noch keine Mitarbeiter</p>
          <p className="text-sm">Über „+ Mitarbeiter" anlegen oder per 📷-Erfassung aus einem Dienstzettel.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {liste.map(m => (
            <div key={m.id}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg bg-gray-50 dark:bg-gray-800 ${m.aktiv ? '' : 'opacity-60'}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {m.nachname} {m.vorname}
                  {!m.aktiv && <span className="ml-2 text-xs text-gray-400">(inaktiv)</span>}
                </p>
                <p className="text-xs text-gray-400">
                  {m.geburtsdatum !== '' ? `geb. ${m.geburtsdatum} · ` : ''}
                  {GESCHLECHT_LABEL[m.geschlecht]} · {apName(m.arbeitsplatz_id)}
                </p>
              </div>
              <button onClick={() => { startEdit(m); }}
                className="text-xs text-gray-400 hover:text-blue-600" title="Bearbeiten">✎</button>
              <button onClick={() => { handleDelete(m); }}
                className="text-xs text-gray-400 hover:text-red-500" title="Löschen">🗑</button>
            </div>
          ))}
          <p className="text-xs text-gray-400 text-center pt-1">
            🔒 {liste.length} Mitarbeiter · Namen/Geburtsdaten AES-256-verschlüsselt
          </p>
        </div>
      )}
    </div>
  );
}
